import React, { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useToast } from '@chakra-ui/toast'
import { FormattedMessage, useIntl } from 'react-intl'
import { Brand } from './Brand.entity'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Input, FormControl, FormLabel, Stack } from '@chakra-ui/react'
import { useCreateBrand, useUpdateBrand } from './queries'

export type BrandModalProps = {
  brand?: Partial<Brand> | undefined
  isOpen: boolean
  onClose: () => void
}

const BrandModal = ({ brand, isOpen, onClose }: BrandModalProps) => {
  const intl = useIntl()
  const toast = useToast()

  const createBrand = useCreateBrand()
  const updateBrand = useUpdateBrand()

  const newBrandTitleMessage = intl.formatMessage({
    id: 'brands.title.new',
    description: 'New brand title message',
    defaultMessage: 'New Brand',
  })

  const createdSuccessfulMessage = intl.formatMessage({
    id: 'brands.createdSuccessfully',
    description: 'brand created successfully toast',
    defaultMessage: 'Brand created successfully',
  })

  const updatedSuccessfulMessage = intl.formatMessage({
    id: 'brands.updatedSuccessfully',
    description: 'Brand updated successfully toast',
    defaultMessage: 'Brand updated successfully',
  })

  const title = brand ? brand.id : newBrandTitleMessage

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Brand>({ defaultValues: brand })

  const onSubmit: SubmitHandler<Brand> = (data) => {
    if (brand) {
      updateBrand.mutate(data)
    } else {
      createBrand.mutate(data)
    }
  }

  useEffect(() => {
    if (createBrand.isSuccess || updateBrand.isSuccess) {
      toast({
        title: updateBrand.isSuccess
          ? updatedSuccessfulMessage
          : createdSuccessfulMessage,
        status: 'success',
        isClosable: true,
        position: 'top-right',
      })
      onClose()
    } else if (createBrand.isError || updateBrand.isError) {
      toast({
        title: 'Error updating or creating Brand, try again later',
        status: 'error',
        isClosable: true,
        position: 'top-right',
      })
    }
  }, [
    createBrand.isSuccess,
    updateBrand.isSuccess,
    createBrand.isError,
    updateBrand.isError,
  ])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Stack spacing="3">
              <FormControl
                id="name"
                isRequired
                isInvalid={errors.name !== undefined}>
                <FormLabel>
                  <FormattedMessage
                    id="brands.formlabel.name"
                    description="Name form label"
                    defaultMessage="Name"
                  />
                </FormLabel>
                <Input {...register('name')}></Input>
              </FormControl>
              <FormControl
                id="ticker"
                isRequired
                isInvalid={errors.ticker !== undefined}>
                <FormLabel>
                  <FormattedMessage
                    id="brands.formlabel.ticker"
                    description="Ticker form label"
                    defaultMessage="Ticker"
                  />
                </FormLabel>
                <Input {...register('ticker')}></Input>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              colorScheme="teal"
              fontSize="md"
              isLoading={createBrand.isLoading || updateBrand.isLoading}>
              <FormattedMessage
                id="brand.button.save"
                description="Brand save button"
                defaultMessage="Save"
              />
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default BrandModal
